<template>
  <div class="flow-navigation-rule">
    <div class="flow-navigation-rule__title">
      <span class="flow-navigation-rule__item">
        <div class="flow-navigation__rules-chevron-wrapper">
          <ui-icon
            class="flow-navigation-rule__chevron-down"
            size="14"
            @click.stop="emit('expand', ruleIndex)"
            icon="fas fa-chevron-down"
            :color="$colors.gray50"
          ></ui-icon>
          <ui-icon
            class="flow-navigation-rule__chevron-right"
            size="14"
            @click.stop="emit('expand', ruleIndex)"
            icon="fas fa-chevron-right"
            :color="$colors.gray50"
          ></ui-icon>
        </div>
        <ui-icon size="14" icon="fas fa-cog" :color="$colors.gray50"></ui-icon>

        <span
          class="pl-4 flow-navigation-rule__condition"
          :class="{ 'flow-navigation-rule__condition--strike': item.status === 'inactive' }"
        >
          <template v-if="typeof name === 'string'">
            {{ translatedName(name) }}
          </template>

          <template v-else-if="name.length > 0">
            <span
              v-for="(contition, index) in name"
              :key="index"
              class="condition__rule"
              :class="{
                'condition__rule--opposite-rule': !contition.elipsis && isOppositeFlowRulePredicate(contition.predicate || ''),
                'condition__rule--elipsis': contition.elipsis
              }"
            > {{ contition.name }} </span>
          </template>

          <template v-else>
            {{ t('Empty Conditions') }}
          </template>

          (# {{ ruleIndex + 1 }})
        </span>
      </span>
      <span class="flow-navigation-rule__actions" v-if="!item.default">
        <div class="flow-navigation__rule-actions">
          <ui-icon
            class="flow-navigation__rule-action-move"
            :color="canMoveUp(ruleIndex) ? $colors.green : $colors.gray20"
            size="14"
            @click.stop="emit('move-rule', ruleIndex, moveDirectionEnum.up)"
            icon="fas fa-chevron-up"
          ></ui-icon>
          <ui-icon
            class="flow-navigation__rule-action-move"
            :color="canMoveDownAlt(ruleIndex, length) ? $colors.green : $colors.gray20"
            size="14"
            @click.stop="emit('move-rule', ruleIndex, moveDirectionEnum.down)"
            icon="fas fa-chevron-down"
          ></ui-icon>
        </div>

        <ui-icon
          size="14"
          class="flow-play-g"
          v-if="item.status !== 'inactive'"
          icon="fas fa-play-circle"
        ></ui-icon>
        <ui-icon
          size="14"
          color="7a7a7a"
          v-if="item.status === 'inactive'"
          icon="fas fa-pause-circle"
        ></ui-icon>
      </span>
    </div>
  </div>
</template>

<script setup lang="ts">
import { useColors } from '@/composables';
import type { RuleError } from '@/composables/useFlowNavigationErrors';
import { MoveDirectionEnum } from '@/services/models/Enums';
import { useFlowNavigationRule } from './useFlowNavigationRule';

const $colors = useColors();

const props = defineProps<{
  item: {
    default?: boolean;
    name?: string;
    status?: string;
    conditions?: Record<string, unknown>;
  };
  ruleIndex: number;
  length: number;
  ruleError?: RuleError;
}>();

const emit = defineEmits<{
  'move-rule': [index: number, direction: MoveDirectionEnum];
  expand: [index: number];
}>();

const {
  t,
  name,
  moveDirectionEnum,
  canMoveUp,
  canMoveDownAlt,
  isOppositeFlowRulePredicate,
  translatedName,
} = useFlowNavigationRule(props);
</script>

<style lang="scss">
.flow-navigation-rule {
  width: 100%;

  // Match develop v-list__tile__title: single-line truncate + actions on the right
  &__title {
    display: flex;
    align-items: center;
    justify-content: space-between;
    width: 100%;
    min-width: 0;
    white-space: nowrap;
  }

  &__chevron-down {
    display: none;
  }

  &__chevron-right {
    display: inline-flex;
  }

  &__item {
    flex: 1 1 auto;
    width: 70%;
    min-width: 0;
    text-overflow: ellipsis;
    display: inline-block;
    overflow: hidden;
    white-space: nowrap;
    vertical-align: middle;
  }

  &__actions {
    flex: 0 0 auto;
    margin-left: 8px;
    display: inline-flex;
    align-items: center;
    float: none;
  }

  &__condition {
    &--strike {
      color: var(--color-gray40);
      text-decoration: line-through;
      text-decoration-line: line-through;
    }
  }
}
</style>
